import React, { useCallback, useRef, useState } from 'react';
import { MASKING_LEVELS, PERCEPTION_BANDS, MASK_MAX_DB } from './tinnitogramLogic';

/**
 * MaskingCurveGraph — perception vs masking noise level line chart.
 *
 * • X = Masking Noise Level (0..80 dB HL)
 * • Y = four categorical bands (top → bottom):
 *     Louder / No Change / Softer / Not Heard
 * • BOTH ears plotted simultaneously:
 *     - Right Ear = solid RED SQUARE, red line
 *     - Left  Ear = solid BLUE DOT, blue line
 * • Ear toggle (activeEar) controls which ear a click writes into.
 * • Click at (nearest dB level, nearest band) → onSetValue(activeEar, db, perception).
 */
const RE_COLOR = '#dc2626';
const LE_COLOR = '#2563eb';

const MaskingCurveGraph = ({
  masking,          // { ear, right: {values: {}}, left: {values: {}} }
  activeEar = 'right',
  onSetValue,       // (ear, dbHL, perception) => void
  readOnly = false,
  width = 460, height = 240,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const marginL = 60, marginR = 12, marginT = 18, marginB = 40;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;
  const bandH = plotH / PERCEPTION_BANDS.length;

  const xScale = useCallback((v) => marginL + (v * plotW) / MASK_MAX_DB, [plotW]);
  const bandCenterY = useCallback((idx) => marginT + bandH * idx + bandH / 2, [bandH]);
  const yInv = useCallback((py) => {
    const raw = (py - marginT) / bandH;
    return Math.max(0, Math.min(PERCEPTION_BANDS.length - 1, Math.floor(raw)));
  }, [bandH]);
  const snapX = (px) => {
    let bestIdx = 0, bestDx = Infinity;
    MASKING_LEVELS.forEach((v, i) => {
      const dx = Math.abs(xScale(v) - px);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    return { db: MASKING_LEVELS[bestIdx] };
  };

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 6 || p.x > width - marginR + 6) return null;
    const { db } = snapX(p.x);
    const bandIdx = yInv(p.y);
    return { db, bandIdx, perception: PERCEPTION_BANDS[bandIdx] };
  };

  const handleClick = (evt) => {
    if (readOnly) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    onSetValue && onSetValue(activeEar, s.db, s.perception);
  };

  const seriesPoints = (earKey) => {
    const values = masking?.[earKey]?.values || {};
    return MASKING_LEVELS
      .map((db) => {
        const p = values[String(db)];
        if (!p) return null;
        const bandIdx = PERCEPTION_BANDS.indexOf(p);
        if (bandIdx < 0) return null;
        return { db, bandIdx, x: xScale(db), y: bandCenterY(bandIdx) };
      })
      .filter(Boolean);
  };

  const rePoints = seriesPoints('right');
  const lePoints = seriesPoints('left');
  const rePath = rePoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const lePath = lePoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const hoverColor = activeEar === 'right' ? RE_COLOR : LE_COLOR;

  return (
    <div className="w-full">
      {/* Legend */}
      <div className="flex items-center gap-3 mb-1 text-[10px] text-slate-600" data-testid="masking-legend">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3" style={{ backgroundColor: RE_COLOR }} data-testid="masking-legend-re-square" />
          <span className="font-semibold" style={{ color: RE_COLOR }}>Right Ear</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: LE_COLOR }} data-testid="masking-legend-le-dot" />
          <span className="font-semibold" style={{ color: LE_COLOR }}>Left Ear</span>
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%" height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={(e) => { const s = snap(toViewBox(e)); setHover(s); }}
        onMouseLeave={() => setHover(null)}
        data-testid="masking-graph"
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {/* Bands */}
        {PERCEPTION_BANDS.map((b, i) => (
          <g key={b}>
            <rect x={marginL} y={marginT + bandH * i} width={plotW} height={bandH} fill={i % 2 ? '#f8fafc' : '#ffffff'} />
            <text x={marginL - 6} y={bandCenterY(i) + 3} fontSize="9.5" fill="#334155" textAnchor="end" fontWeight="600">{b}</text>
          </g>
        ))}
        <line x1={marginL} x2={width - marginR} y1={marginT + plotH} y2={marginT + plotH} stroke="#94a3b8" strokeWidth="0.6" />
        {/* X ticks */}
        {MASKING_LEVELS.map((db) => (
          <g key={db}>
            <line x1={xScale(db)} x2={xScale(db)} y1={marginT} y2={marginT + plotH} stroke="#f1f5f9" strokeWidth="0.5" />
            <text x={xScale(db)} y={marginT + plotH + 14} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">{db}</text>
          </g>
        ))}
        <text x={marginL - 44} y={marginT + plotH / 2 - 20} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 44}, ${marginT + plotH / 2})`} textAnchor="middle">Tinnitus Perception</text>
        <text x={marginL + plotW / 2} y={height - 4} fontSize="9" fill="#475569" textAnchor="middle">Masking Noise Level (dB HL)</text>

        {/* Right Ear series — solid RED SQUARE + red line */}
        {rePath && <path d={rePath} fill="none" stroke={RE_COLOR} strokeWidth="2" data-testid="masking-line-re" />}
        {rePoints.map((p) => (
          <rect key={`re-${p.db}`} x={p.x - 4} y={p.y - 4} width="8" height="8" fill={RE_COLOR} data-testid={`masking-point-re-${p.db}`} />
        ))}

        {/* Left Ear series — solid BLUE DOT + blue line */}
        {lePath && <path d={lePath} fill="none" stroke={LE_COLOR} strokeWidth="2" data-testid="masking-line-le" />}
        {lePoints.map((p) => (
          <circle key={`le-${p.db}`} cx={p.x} cy={p.y} r="4.5" fill={LE_COLOR} data-testid={`masking-point-le-${p.db}`} />
        ))}

        {/* Hover — ghost marker in ACTIVE ear shape/color */}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="masking-hover">
            <line x1={xScale(hover.db)} x2={xScale(hover.db)} y1={marginT} y2={marginT + plotH} stroke={hoverColor} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
            {activeEar === 'right'
              ? <rect x={xScale(hover.db) - 4} y={bandCenterY(hover.bandIdx) - 4} width="8" height="8" fill={hoverColor} opacity="0.75" />
              : <circle cx={xScale(hover.db)} cy={bandCenterY(hover.bandIdx)} r="4.5" fill={hoverColor} opacity="0.75" />
            }
            <rect x={xScale(hover.db) + 6} y={bandCenterY(hover.bandIdx) - 10} width="82" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={xScale(hover.db) + 8} y={bandCenterY(hover.bandIdx)} fontSize="9" fill="#ffffff" fontWeight="600">{activeEar === 'right' ? 'RE' : 'LE'} · {hover.db} · {hover.perception}</text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default MaskingCurveGraph;
